import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  FileText, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase,
  GraduationCap,
  Clock,
  Trash2,
  Download,
  Eye,
  Loader2,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800' },
  reviewing: { label: 'İnceleniyor', color: 'bg-blue-100 text-blue-800' },
  interview: { label: 'Mülakat', color: 'bg-purple-100 text-purple-800' },
  hired: { label: 'İşe Alındı', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-800' },
};

const AdminJobApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('eta_admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/job-applications`, {
        headers: getAuthHeaders()
      });
      setApplications(response.data);
    } catch (error) {
      toast.error('Başvurular yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/job-applications/${appId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setApplications(applications.map(app => 
        app.id === appId ? { ...app, status: newStatus } : app
      ));
      toast.success('Durum güncellendi');
    } catch (error) {
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const handleMarkAsRead = async (appId) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/job-applications/${appId}/read`,
        {},
        { headers: getAuthHeaders() }
      );
      setApplications(applications.map(app => 
        app.id === appId ? { ...app, is_read: true } : app
      ));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (appId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/job-applications/${appId}`, {
        headers: getAuthHeaders()
      });
      setApplications(applications.filter(app => app.id !== appId));
      setSelectedApp(null);
      toast.success('Başvuru silindi');
    } catch (error) {
      toast.error('Başvuru silinirken hata oluştu');
    }
  };

  const viewApplication = (app) => {
    setSelectedApp(app);
    if (!app.is_read) {
      handleMarkAsRead(app.id);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = applications.filter(app => !app.is_read).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-job-applications">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              İş Başvuruları
            </h1>
            <p className="text-slate-500 mt-1">
              Toplam {applications.length} başvuru
              {unreadCount > 0 && ` • ${unreadCount} yeni`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
          </div>
        ) : applications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Henüz iş başvurusu yok</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Application List */}
            <div className="space-y-4">
              {applications.map((app) => (
                <Card 
                  key={app.id}
                  data-testid={`job-application-${app.id}`}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    !app.is_read ? 'border-l-4 border-l-[#005CB2]' : ''
                  } ${selectedApp?.id === app.id ? 'ring-2 ring-[#005CB2]' : ''}`}
                  onClick={() => viewApplication(app)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#005CB2]/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-[#005CB2]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                            {app.full_name}
                            {!app.is_read && (
                              <span className="w-2 h-2 bg-[#005CB2] rounded-full" />
                            )}
                          </h3>
                          <p className="text-sm text-slate-500">{app.desired_position}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatDate(app.created_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className={statusConfig[app.status || 'pending'].color}>
                        {statusConfig[app.status || 'pending'].label}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Application Detail */}
            {selectedApp ? (
              <Card className="sticky top-4" data-testid="job-application-detail">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Başvuru Detayı
                    </CardTitle>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Başvuruyu Sil</AlertDialogTitle>
                          <AlertDialogDescription>
                            Bu başvuruyu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İptal</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(selectedApp.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Sil
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Status */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Durum
                    </label>
                    <Select
                      value={selectedApp.status || 'pending'}
                      onValueChange={(value) => handleStatusChange(selectedApp.id, value)}
                    >
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusConfig).map(([key, { label }]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Applicant Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Ad Soyad</p>
                        <p className="font-medium">{selectedApp.full_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">E-posta</p>
                        <a href={`mailto:${selectedApp.email}`} className="font-medium text-[#005CB2] hover:underline">
                          {selectedApp.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Telefon</p>
                        <a href={`tel:${selectedApp.phone}`} className="font-medium">
                          {selectedApp.phone}
                        </a>
                      </div>
                    </div>

                    {selectedApp.address && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Adres</p>
                          <p className="font-medium">{selectedApp.address}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Başvurulan Pozisyon</p>
                        <p className="font-medium">{selectedApp.desired_position}</p>
                      </div>
                    </div>

                    {selectedApp.education && (
                      <div className="flex items-center gap-3">
                        <GraduationCap className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Eğitim</p>
                          <p className="font-medium">{selectedApp.education}</p>
                        </div>
                      </div>
                    )}

                    {selectedApp.experience && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-slate-500">Deneyim</p>
                          <p className="font-medium whitespace-pre-wrap">{selectedApp.experience}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CV Download */}
                  {selectedApp.cv_path && (
                    <div className="pt-4 border-t">
                      <a
                        href={`${API_URL}${selectedApp.cv_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#005CB2] text-white rounded-lg hover:bg-[#004a91] transition-colors"
                        data-testid="download-cv-btn"
                      >
                        <Download className="w-4 h-4" />
                        CV'yi İndir
                      </a>
                    </div>
                  )}

                  {/* Date */}
                  <div className="pt-4 border-t text-sm text-slate-500">
                    <Clock className="w-4 h-4 inline mr-2" />
                    {formatDate(selectedApp.created_at)}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Detay görüntülemek için bir başvuru seçin</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminJobApplications;
